import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon, Check, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/base/popover';
import { Separator } from '@/ui/base/separator';

interface MonthFilterProps {
  /** Competencia no formato yyyy-mm. Vazio = nenhuma. */
  value: string;
  onChange: (competencia: string) => void;
  label?: string;
  id?: string;
  className?: string;
  /** Rotulo continua acessivel (sr-only), so nao aparece visualmente — para
   *  encaixar num cabecalho de tela compacto, ao lado de outros controles. */
  hideLabel?: boolean;
  /** Quantos meses recentes listar como atalho (inclui o mês atual). Default 6. */
  mesesRecentes?: number;
}

const NOMES_MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const NOMES_MES_LONGO = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function paraCompetencia(ano: number, mes: number): string {
  return `${ano}-${String(mes).padStart(2, '0')}`;
}

function rotuloCurto(competencia: string): string {
  const [ano, mes] = competencia.split('-').map(Number);
  if (!ano || !mes) return competencia;
  return `${NOMES_MES_CURTO[mes - 1]}/${String(ano).slice(2)}`;
}

function rotuloLongo(competencia: string): string {
  const [ano, mes] = competencia.split('-').map(Number);
  if (!ano || !mes) return competencia;
  return `${NOMES_MES_LONGO[mes - 1]} de ${ano}`;
}

/**
 * Filtro por competencia mensal — o mes de referencia da apuracao. Muita coisa
 * no TEG e mensal (apuracao, remuneracao, cobranca), e a competencia e sempre
 * um mes inteiro, nunca um dia (docs/DESIGN-SYSTEM.md, secao 5).
 *
 * Botão + popover (atalhos de meses recentes + grade de mês/ano), copiado do
 * protótipo — o `<input type="month">` nativo anterior renderiza diferente
 * em cada navegador e não bate com o design do resto da tela.
 */
export function MonthFilter({
  value,
  onChange,
  label = 'Competência',
  id = 'competencia',
  className,
  hideLabel = false,
  mesesRecentes = 6,
}: MonthFilterProps) {
  const [aberto, definirAberto] = useState(false);

  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;

  const atalhos = useMemo(
    () =>
      Array.from({ length: mesesRecentes }, (_, i) => {
        const data = new Date(anoAtual, hoje.getMonth() - i, 1);
        return paraCompetencia(data.getFullYear(), data.getMonth() + 1);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mesesRecentes],
  );

  const [anoGrade, definirAnoGrade] = useState(() => (value ? Number(value.slice(0, 4)) : anoAtual));

  function selecionar(competencia: string) {
    onChange(competencia);
    definirAberto(false);
  }

  const idRotulo = `${id}-rotulo`;
  const idValor = `${id}-valor`;

  return (
    <div className={cn(hideLabel ? '' : 'space-y-1', className)}>
      {/* O rotulo existe sempre; com `hideLabel` ele so deixa de aparecer. E o
          gatilho e um <button>, que `htmlFor` nao rotula — por isso o nome
          acessivel vem de `aria-labelledby`, apontando para o rotulo e para o
          texto do valor: "Competencia jul/26". O valor entra no nome de
          proposito: WCAG 2.5.3 (Label in Name) pede que o nome acessivel
          contenha o texto visivel do controle. */}
      <Label
        id={idRotulo}
        htmlFor={id}
        className={cn(
          hideLabel
            ? 'sr-only'
            : 'block text-xs font-medium uppercase tracking-wider text-muted-foreground',
        )}
      >
        {label}
      </Label>
      <Popover open={aberto} onOpenChange={definirAberto}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            aria-labelledby={`${idRotulo} ${idValor}`}
            className={cn('h-10 w-40 justify-start text-left font-normal capitalize', !value && 'text-muted-foreground')}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
            <span id={idValor} className="truncate">
              {value ? rotuloCurto(value) : 'Todos'}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <div className="space-y-1 p-2">
            <button
              type="button"
              onClick={() => selecionar('')}
              className={cn(
                'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-accent',
                value === '' && 'bg-accent font-medium',
              )}
            >
              <span>Todos</span>
              {value === '' && <Check className="h-4 w-4" aria-hidden="true" />}
            </button>
            <div className="px-2 pb-0.5 pt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              Últimos {mesesRecentes} meses
            </div>
            {atalhos.map((competencia) => {
              const selecionado = value === competencia;
              return (
                <button
                  key={competencia}
                  type="button"
                  onClick={() => selecionar(competencia)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm capitalize hover:bg-accent',
                    selecionado && 'bg-accent font-medium',
                  )}
                >
                  <span>{rotuloLongo(competencia)}</span>
                  {selecionado && <Check className="h-4 w-4" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
          <Separator />
          <div className="space-y-2 p-2">
            <div className="px-1 text-[10px] uppercase tracking-wider text-muted-foreground">Outro mês</div>
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => definirAnoGrade((a) => a - 1)}
                aria-label="Ano anterior"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <span className="text-sm font-medium tabular-nums">{anoGrade}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => definirAnoGrade((a) => a + 1)}
                aria-label="Próximo ano"
                disabled={anoGrade >= anoAtual}
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {NOMES_MES_CURTO.map((nome, indice) => {
                const numeroMes = indice + 1;
                const competencia = paraCompetencia(anoGrade, numeroMes);
                const futuro = anoGrade > anoAtual || (anoGrade === anoAtual && numeroMes > mesAtual);
                const selecionado = value === competencia;
                return (
                  <button
                    key={competencia}
                    type="button"
                    disabled={futuro}
                    onClick={() => selecionar(competencia)}
                    className={cn(
                      'rounded-md px-2 py-1.5 text-sm capitalize hover:bg-accent',
                      'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
                      selecionado && 'bg-primary text-primary-foreground hover:bg-primary',
                    )}
                  >
                    {nome}
                  </button>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
